import React, { useEffect, useRef, useState } from 'react';
import { Alert, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { Button, ErrorResponse } from '~components';

const DEFAULT_CONFIRM_PHRASE = 'CONFIRM';

// Heading above the consequence bullets, inside the danger alert. Shared by both dialogs
// (rollover and elect-to-return) so the acknowledgement framing reads identically for each.
const WARNINGS_TITLE = 'Please Acknowledge and Confirm The Following:';

interface ComponentProps {
  isOpen: boolean;
  title: string;
  /** Label/value pairs rendered as a <dl> above the warnings, naming what the action applies to. */
  details?: { label: string; value: string }[];
  /** Consequence bullets, rendered as an <Alert color="danger"> list. */
  warnings: string[];
  /** Checkbox label. */
  acknowledgement: string;
  /** Compared CASE-SENSITIVELY. Defaults to 'CONFIRM'. */
  confirmPhrase?: string;
  confirmLabel: string;
  submittingLabel: string;
  /** Parsed SA error from a failed attempt — the dialog stays open so the user can retry. */
  error?: string | null;
  isSubmitting?: boolean;
  dataCy: string;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
}

// TAV-2601 / TAV-2602 — the shared gated dialog for irreversible SA-mutating budget actions
// (rollover, elect-to-return). One component, not two: the two dialogs differ only in copy, gate
// strictness ('either' vs 'both') and button labels, and no typed-CONFIRM / checkbox-gated
// pattern existed anywhere in this repo before this ticket.
//
// Modal shell (backdrop lock, keyboard lock, Cancel disabled) mirrors EligibilityConfirmDialog.tsx
// verbatim; the confirm button itself uses `loading` (Button's in-place spinner) rather than a
// button-text swap, so it does not resize while submitting.
const DestructiveConfirmDialog: React.FC<ComponentProps> = ({
  isOpen,
  title,
  details,
  warnings,
  acknowledgement,
  confirmPhrase = DEFAULT_CONFIRM_PHRASE,
  confirmLabel,
  submittingLabel,
  error,
  isSubmitting = false,
  dataCy,
  onClose,
  onConfirm,
}) => {
  const [checked, setChecked] = useState(false);
  const [typed, setTyped] = useState('');

  // Synchronous re-entrancy guard: `isSubmitting` only flips true once the async onConfirm's
  // caller updates state, which happens on a later render, so a second click landing before that
  // render would still read the stale `canConfirm` and fire a second irreversible SA call. A ref
  // is checked and set synchronously inside the same click handler invocation, closing that gap
  // regardless of render timing.
  const isSubmittingRef = useRef(false);

  // Reset the gate every time the dialog opens. Without this, cancelling and reopening leaves it
  // pre-armed from a previous attempt — a one-click irreversible action. This is a safety
  // requirement, not polish.
  useEffect(() => {
    if (isOpen) {
      setChecked(false);
      setTyped('');
    }
  }, [isOpen]);

  // BOTH gates must be satisfied — the checkbox AND the typed phrase, never one or the other.
  // TAV-2601's acceptance criteria worded the rollover gate as "type CONFIRM *or* tick a checkbox";
  // this is deliberately stricter, at Cole's instruction, so both irreversible actions demand the
  // same two-part proof of intent. Note the comparison is case-sensitive: 'confirm' does not pass.
  const typedMatches = typed === confirmPhrase;
  const canConfirm = checked && typedMatches && !isSubmitting;

  const handleConfirm = async () => {
    if (isSubmittingRef.current || !canConfirm) return;
    isSubmittingRef.current = true;
    try {
      await onConfirm();
    } finally {
      isSubmittingRef.current = false;
    }
  };

  // While submitting, lock the modal closed: backdrop clicks, Esc and the header X all become
  // no-ops (Cancel is separately disabled below) — without this a mid-flight close could let the
  // user retry and double-fire an irreversible SA call.
  const dismiss = isSubmitting ? undefined : onClose;
  const checkboxId = `${dataCy}-checkbox`;
  const typedInputId = `${dataCy}-typed`;
  const headerId = `${dataCy}-header`;

  return (
    <Modal
      isOpen={isOpen}
      toggle={dismiss}
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}
      size="lg"
      data-cy={dataCy}
      aria-labelledby={headerId}
      labelledBy={headerId}
    >
      {/* Red header, matching the destructive-confirmation precedent in
          AppointmentCancellationConfirm.tsx. btn-close-white + data-bs-theme="dark" are required
          because this header carries a toggle: without them the close X renders dark-on-red and is
          all but invisible (same reason ServiceColour.tsx adds them). */}
      <ModalHeader
        toggle={dismiss}
        id={headerId}
        className="bg-danger text-white btn-close-white"
        data-bs-theme="dark"
      >
        {title}
      </ModalHeader>
      <ModalBody>
        {details && details.length > 0 && (
          <dl className="row mb-3" data-cy={`${dataCy}-details`}>
            {details.map(({ label, value }) => (
              <React.Fragment key={label}>
                <dt className="col-4 fw-normal text-muted">{label}</dt>
                <dd className="col-8 mb-1 fw-semibold">{value}</dd>
              </React.Fragment>
            ))}
          </dl>
        )}
        {warnings.length > 0 && (
          <Alert color="danger" className="mb-3" data-cy={`${dataCy}-warnings`}>
            <h5 className="alert-heading fw-bold mb-2" data-cy={`${dataCy}-warnings-title`}>
              {WARNINGS_TITLE}
            </h5>
            <ul className="mb-0 ps-3">
              {warnings.map((warning, index) => (
                <li key={`${dataCy}-warning-${index}`}>{warning}</li>
              ))}
            </ul>
          </Alert>
        )}
        <FormGroup check className="mb-3 d-flex align-items-center">
          <Input
            type="checkbox"
            id={checkboxId}
            checked={checked}
            onChange={(event) => setChecked(event.target.checked)}
            disabled={isSubmitting}
            className="mt-0"
            data-cy={`${dataCy}-checkbox-input`}
          />
          <Label for={checkboxId} check className="ms-2 mb-0">
            {acknowledgement}
          </Label>
        </FormGroup>
        <FormGroup className="mb-0">
          <Label for={typedInputId}>
            Type <strong>{confirmPhrase}</strong> to proceed
          </Label>
          <Input
            type="text"
            id={typedInputId}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            disabled={isSubmitting}
            data-cy={`${dataCy}-typed-input`}
          />
        </FormGroup>
        <ErrorResponse message={error} className="mt-3 mb-0" dataCy={`${dataCy}-error`} />
      </ModalBody>
      <ModalFooter>
        <Button
          color="danger"
          onClick={handleConfirm}
          disabled={!canConfirm}
          loading={isSubmitting}
          aria-label={isSubmitting ? submittingLabel : confirmLabel}
          dataCy={`${dataCy}-confirm-button`}
        >
          {confirmLabel}
        </Button>
        <Button color="info" onClick={onClose} disabled={isSubmitting} dataCy={`${dataCy}-cancel-button`}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DestructiveConfirmDialog;
